import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { InvoiceDetailRecord, InvoiceStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { CollectionTable } from '@renderer/shared/components/collection-table';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  OverflowMenuButton,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { areDraftArraysEqual, areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { deleteInvoices, getInvoiceById, replaceInvoiceLineItems, syncInvoiceToOblio, updateInvoice } from './invoices-repository';
import { selectInvoiceEditorModel } from './invoices-selectors';
import { useInvoicesViewStore } from './invoices-view-store';

function formatSnapshotAddress(value: InvoiceDetailRecord['invoice']['customerSnapshot'], fallback: string): string {
  return value.billingAddress?.formatted ?? fallback;
}

function formatSnapshotRepresentative(value: InvoiceDetailRecord['invoice']['customerSnapshot'], fallback: string): string {
  if (!value.representative.name) {
    return fallback;
  }

  if (!value.representative.role) {
    return value.representative.name;
  }

  return `${value.representative.name} - ${value.representative.role}`;
}

interface InvoiceGeneralDraft {
  status: InvoiceStatus;
  dueDate: string;
  paymentNotes: string;
  paidAt: string;
  paidAmount: string;
}

interface InvoiceLineItemDraft {
  id: string;
  name: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  vatRate: string;
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function toGeneralDraft(detail: InvoiceDetailRecord): InvoiceGeneralDraft {
  return {
    status: detail.invoice.status,
    dueDate: formatDateInputValue(detail.invoice.dueDate),
    paymentNotes: detail.invoice.paymentNotes ?? '',
    paidAt: formatDateInputValue(detail.invoice.paidAt),
    paidAmount: detail.invoice.paidAmount === null ? '' : String(detail.invoice.paidAmount)
  };
}

function toLineItemsDraft(detail: InvoiceDetailRecord): InvoiceLineItemDraft[] {
  return detail.lineItems.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description ?? '',
    quantity: String(item.quantity),
    unit: item.unit,
    unitPrice: String(item.unitPrice),
    vatRate: String(item.vatRate)
  }));
}

export function InvoiceEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { locale, t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = useInvoicesViewStore();
  const [detail, setDetail] = useState<InvoiceDetailRecord | null>(null);
  const [generalDraft, setGeneralDraft] = useState<InvoiceGeneralDraft | null>(null);
  const [lineItemsDraft, setLineItemsDraft] = useState<InvoiceLineItemDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncingOblio, setIsSyncingOblio] = useState(false);
  const savedGeneralDraft = useMemo(() => (detail ? toGeneralDraft(detail) : null), [detail]);
  const savedLineItemsDraft = useMemo(() => (detail ? toLineItemsDraft(detail) : null), [detail]);

  useEffect(() => {
    if (!invoiceId) {
      setError('Missing invoice id.');
      return;
    }

    let active = true;
    void getInvoiceById(invoiceId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Invoice not found.');
          return;
        }

        setDetail(nextDetail);
        setGeneralDraft(toGeneralDraft(nextDetail));
        setLineItemsDraft(toLineItemsDraft(nextDetail));
        setRouteDetailTitle(`${nextDetail.invoice.series} ${nextDetail.invoice.number}`);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Invoice unavailable.');
        }
      });

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [invoiceId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'invoices' || !detail || !detailRequest.ids.includes(detail.invoice.id)) {
          return;
        }

        void deleteInvoices({ ids: [detail.invoice.id] }).then(() => navigate('/invoices'));
      }),
    [detail, navigate]
  );

  const generalDirty = generalDraft && savedGeneralDraft ? !areDraftsEqual(generalDraft, savedGeneralDraft) : false;
  const lineItemsDirty =
    lineItemsDraft && savedLineItemsDraft ? !areDraftArraysEqual(lineItemsDraft, savedLineItemsDraft) : false;

  useRegisterEditorSaveHandler(
    detail
      ? () => {
          if (isSaving) {
            return;
          }

          if (lineItemsDirty) {
            void handleSaveLineItems();
            return;
          }

          if (generalDirty) {
            void handleSaveGeneral();
          }
        }
      : null
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('invoices.title')} description={error} />
      </div>
    );
  }

  if (!detail) {
    return <div className="av-page" />;
  }

  const model = selectInvoiceEditorModel(detail, { locale, t });
  const invoiceIdToDelete = detail.invoice.id;
  const statusOptions = (['draft', 'issued', 'paid', 'overdue', 'archived'] satisfies InvoiceStatus[]).map((value) => ({
    value,
    label: t(`enums.invoiceStatus.${value}`)
  }));

  async function handleDeleteInvoice(): Promise<void> {
    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deleteInvoices({ ids: [invoiceIdToDelete] });
    navigate('/invoices');
  }

  async function handleSaveGeneral(): Promise<void> {
    if (!generalDraft || !detail) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const paidAmount = generalDraft.paidAmount.trim() ? Number(generalDraft.paidAmount) : null;
      if (paidAmount !== null && Number.isNaN(paidAmount)) {
        throw new Error('Invalid paid amount.');
      }

      const detailResponse = await updateInvoice({
        id: detail.invoice.id,
        status: generalDraft.status,
        dueDate: toIsoDate(generalDraft.dueDate),
        paymentNotes: generalDraft.paymentNotes.trim() ? generalDraft.paymentNotes : null,
        paidAt: toIsoDate(generalDraft.paidAt),
        paidAmount
      });

      setDetail(detailResponse);
      setGeneralDraft(toGeneralDraft(detailResponse));
      setLineItemsDraft(toLineItemsDraft(detailResponse));
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancelGeneral(): void {
    if (!detail) {
      return;
    }

    setSaveError(null);
    setGeneralDraft(toGeneralDraft(detail));
  }

  async function handleSaveLineItems(): Promise<void> {
    if (!lineItemsDraft || !detail) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const payloadLineItems = lineItemsDraft.map((item, index) => {
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unitPrice);
        const vatRate = Number(item.vatRate);

        if (!item.name.trim()) {
          throw new Error('Line item name is required.');
        }
        if (Number.isNaN(quantity) || quantity <= 0) {
          throw new Error('Invalid quantity.');
        }
        if (Number.isNaN(unitPrice) || unitPrice < 0) {
          throw new Error('Invalid unit price.');
        }
        if (Number.isNaN(vatRate) || vatRate < 0) {
          throw new Error('Invalid VAT rate.');
        }

        return {
          id: item.id,
          position: index,
          name: item.name.trim(),
          description: item.description.trim() ? item.description : null,
          quantity,
          unit: item.unit.trim() || 'buc',
          unitPrice,
          vatRate
        };
      });

      const detailResponse = await replaceInvoiceLineItems({
        invoiceId: detail.invoice.id,
        lineItems: payloadLineItems
      });

      setDetail(detailResponse);
      setGeneralDraft(toGeneralDraft(detailResponse));
      setLineItemsDraft(toLineItemsDraft(detailResponse));
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSyncOblio(): Promise<void> {
    if (!detail) {
      return;
    }

    setIsSyncingOblio(true);
    setSaveError(null);

    try {
      const detailResponse = await syncInvoiceToOblio(detail.invoice.id);
      setDetail(detailResponse);
      setGeneralDraft(toGeneralDraft(detailResponse));
      setLineItemsDraft(toLineItemsDraft(detailResponse));
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Oblio sync failed.');
    } finally {
      setIsSyncingOblio(false);
    }
  }

  function handleCancelLineItems(): void {
    if (!detail) {
      return;
    }

    setSaveError(null);
    setLineItemsDraft(toLineItemsDraft(detail));
  }

  function handleAddLineItem(): void {
    setLineItemsDraft((current) => {
      const next = current ? [...current] : [];
      next.push({
        id: crypto.randomUUID(),
        name: '',
        description: '',
        quantity: '1',
        unit: 'buc',
        unitPrice: '0',
        vatRate: '19'
      });
      return next;
    });
  }

  function handleRemoveLineItem(id: string): void {
    setLineItemsDraft((current) => (current ? current.filter((item) => item.id !== id) : current));
  }

  function moveLineItem(id: string, direction: -1 | 1): void {
    setLineItemsDraft((current) => {
      if (!current) {
        return current;
      }

      const index = current.findIndex((item) => item.id === id);
      if (index === -1) {
        return current;
      }

      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [item] = next.splice(index, 1);
      if (!item) {
        return current;
      }
      next.splice(targetIndex, 0, item);
      return next;
    });
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={() => void handleDeleteInvoice()}>
                <Icon name="trash" />
                <span>{t('shell.delete')}</span>
              </button>
              <OverflowMenuButton />
            </>
          }
        >
          <div className="av-editor-hero-card__content">
            <div className="av-editor-hero-card__identity">
              <div className="av-editor-hero-card__icon">
                <Icon name="invoices" />
              </div>
              <div className="av-editor-hero-card__identity-copy">
                <h2>{model.title}</h2>
                <div className="av-editor-hero-card__meta">
                  <StatusBadge label={model.statusLabel} tone={model.statusTone} />
                  <StatusBadge label={model.grandTotalLabel} tone="dark" />
                </div>
              </div>
            </div>

            <div className="av-editor-hero-card__endpoints">
              <div className="av-endpoint-row">
                <Icon name="customers" />
                <span>{model.customerName}</span>
              </div>
              <div className="av-endpoint-row">
                <Icon name="document" />
                <span>{model.issuerName}</span>
              </div>
              <div className="av-endpoint-row">
                <Icon name="calendar" />
                <span>{model.dueDateLabel}</span>
              </div>
            </div>
          </div>
        </EditorHeroCard>
      }
      heroAside={
        <EditorSectionCard title={t('invoices.sections.summary')} actions={<OverflowMenuButton />}>
          <div className="av-summary-stack">
            <ValuePair label={t('invoices.summary.issueDate')} value={model.issueDateLabel} />
            <ValuePair label={t('invoices.summary.dueDate')} value={model.dueDateLabel} />
            <ValuePair label={t('invoices.summary.total')} value={model.grandTotalLabel} />
            <ValuePair label={t('invoices.summary.sync')} value={model.syncLabel} />
          </div>
        </EditorSectionCard>
      }
      tabs={
        <EditorTabs
          tabs={[
            { id: 'general', label: t('invoices.tabs.general'), icon: 'invoices' },
            { id: 'lineItems', label: t('invoices.tabs.lineItems'), icon: 'list' },
            { id: 'document', label: t('invoices.tabs.document'), icon: 'document' }
          ]}
          value={editorTab}
          onChange={setEditorTab}
        />
      }
    >
      <EditorBodyLayout
        main={
          <>
            {saveError ? <div className="av-inline-error">{saveError}</div> : null}
            {editorTab === 'general' ? (
              <>
                <EditorSectionCard
                  title={t('invoices.sections.general')}
                  actions={
                    <div className="av-inline-metadata">
                      <button className="av-secondary-button" type="button" onClick={handleCancelGeneral} disabled={!generalDirty || isSaving}>
                        {t('common.cancel')}
                      </button>
                      <button className="av-primary-button" type="button" onClick={() => void handleSaveGeneral()} disabled={!generalDirty || isSaving}>
                        {t('common.applyChanges')}
                      </button>
                    </div>
                  }
                >
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('invoices.fields.series')}</span>
                      <input className="av-input" readOnly type="text" value={detail.invoice.series} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.number')}</span>
                      <input className="av-input" readOnly type="text" value={detail.invoice.number} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.issueDate')}</span>
                      <input className="av-input" readOnly type="text" value={model.issueDateLabel} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.dueDate')}</span>
                      <TextField
                        type="date"
                        value={generalDraft?.dueDate ?? ''}
                        onValueChange={(value) => setGeneralDraft((current) => (current ? { ...current, dueDate: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.status')}</span>
                      <SelectField
                        value={generalDraft?.status ?? detail.invoice.status}
                        options={statusOptions}
                        onChange={(event) =>
                          setGeneralDraft((current) => (current ? { ...current, status: event.target.value as InvoiceStatus } : current))
                        }
                      />
                    </label>
                    <div className="av-form-field">
                      <span>{t('invoices.fields.customer')}</span>
                      <Link className="av-inline-link av-inline-link--field" to={`/customers/${detail.invoice.customerId}`}>
                        <span>{model.customerName}</span>
                        <Icon name="chevron" />
                      </Link>
                    </div>
                    <div className="av-form-field">
                      <span>{t('invoices.fields.project')}</span>
                      {detail.invoice.projectId ? (
                        <Link className="av-inline-link av-inline-link--field" to={`/projects/${detail.invoice.projectId}`}>
                          <span>{model.projectName}</span>
                          <Icon name="chevron" />
                        </Link>
                      ) : (
                        <div className="av-readonly-text-block">{t('common.notSet')}</div>
                      )}
                    </div>
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('invoices.sections.parties')} actions={<OverflowMenuButton />}>
                  <div className="av-customer-secondary-grid">
                    <div className="av-summary-stack">
                      <strong>{t('invoices.parties.issuer')}</strong>
                      <span>{detail.invoice.issuerSnapshot.legalName}</span>
                      <span>{detail.invoice.issuerSnapshot.cui ?? t('common.notSet')}</span>
                      <span>{formatSnapshotAddress(detail.invoice.issuerSnapshot, t('common.notSet'))}</span>
                      <span>{formatSnapshotRepresentative(detail.invoice.issuerSnapshot, t('common.notSet'))}</span>
                    </div>
                    <div className="av-summary-stack">
                      <strong>{t('invoices.parties.customer')}</strong>
                      <span>{detail.invoice.customerSnapshot.legalName}</span>
                      <span>{detail.invoice.customerSnapshot.cui ?? t('common.notSet')}</span>
                      <span>{detail.invoice.customerSnapshot.registrationNumber ?? t('common.notSet')}</span>
                      <span>{formatSnapshotAddress(detail.invoice.customerSnapshot, t('common.notSet'))}</span>
                      <span>{formatSnapshotRepresentative(detail.invoice.customerSnapshot, t('common.notSet'))}</span>
                    </div>
                  </div>
                </EditorSectionCard>

                <EditorSectionCard title={t('invoices.sections.paymentTax')} actions={<OverflowMenuButton />}>
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('invoices.fields.currency')}</span>
                      <input className="av-input" readOnly type="text" value={detail.invoice.currency} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.subtotal')}</span>
                      <input className="av-input" readOnly type="text" value={model.subtotalLabel} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.taxTotal')}</span>
                      <input className="av-input" readOnly type="text" value={model.taxLabel} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.grandTotal')}</span>
                      <input className="av-input" readOnly type="text" value={model.grandTotalLabel} />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.paidAt')}</span>
                      <TextField
                        type="date"
                        value={generalDraft?.paidAt ?? ''}
                        onValueChange={(value) => setGeneralDraft((current) => (current ? { ...current, paidAt: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('invoices.fields.paidAmount')}</span>
                      <TextField
                        type="number"
                        min={0}
                        step="0.01"
                        value={generalDraft?.paidAmount ?? ''}
                        onValueChange={(value) => setGeneralDraft((current) => (current ? { ...current, paidAmount: value } : current))}
                      />
                    </label>
                  </div>
                  <div className="av-form-field">
                    <span>{t('invoices.fields.paymentNotes')}</span>
                    <TextAreaField
                      rows={4}
                      value={generalDraft?.paymentNotes ?? ''}
                      onValueChange={(value) => setGeneralDraft((current) => (current ? { ...current, paymentNotes: value } : current))}
                    />
                  </div>
                </EditorSectionCard>
              </>
            ) : null}

            {editorTab === 'lineItems' ? (
              <EditorSectionCard
                title={t('invoices.sections.lineItems')}
                actions={
                  <div className="av-inline-metadata">
                    <button className="av-secondary-button" type="button" onClick={handleAddLineItem} disabled={isSaving}>
                      <Icon name="plus" />
                      <span>{t('common.add')}</span>
                    </button>
                    <button className="av-secondary-button" type="button" onClick={handleCancelLineItems} disabled={!lineItemsDirty || isSaving}>
                      {t('common.cancel')}
                    </button>
                    <button className="av-primary-button" type="button" onClick={() => void handleSaveLineItems()} disabled={!lineItemsDirty || isSaving}>
                      {t('common.applyChanges')}
                    </button>
                  </div>
                }
              >
                <CollectionTable
                  ariaLabel={t('invoices.sections.lineItems')}
                  columns={[
                    { id: 'name', label: t('invoices.lineItems.columns.name') },
                    { id: 'qty', label: t('invoices.lineItems.columns.quantity') },
                    { id: 'price', label: t('invoices.lineItems.columns.unitPrice'), align: 'right' },
                    { id: 'vat', label: t('invoices.lineItems.columns.vat'), align: 'right' },
                    { id: 'total', label: t('invoices.lineItems.columns.lineTotal'), align: 'right' },
                    { id: 'actions', label: '', align: 'right' }
                  ]}
                >
                  {(lineItemsDraft ?? []).map((item) => {
                    const quantity = Number(item.quantity) || 0;
                    const unitPrice = Number(item.unitPrice) || 0;
                    const lineTotal = quantity * unitPrice;

                    return (
                      <tr key={item.id} className="av-collection-table__row">
                        <td className="av-collection-table__cell">
                          <div className="av-summary-stack">
                            <TextField
                              value={item.name}
                              onValueChange={(value) =>
                                setLineItemsDraft((current) =>
                                  current
                                    ? current.map((row) => (row.id === item.id ? { ...row, name: value } : row))
                                    : current
                                )
                              }
                            />
                            <TextField
                              placeholder={t('invoices.lineItems.columns.description')}
                              value={item.description}
                              onValueChange={(value) =>
                                setLineItemsDraft((current) =>
                                  current
                                    ? current.map((row) => (row.id === item.id ? { ...row, description: value } : row))
                                    : current
                                )
                              }
                            />
                          </div>
                        </td>
                        <td className="av-collection-table__cell">
                          <div className="av-form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <TextField
                              type="number"
                              min={0}
                              step="0.01"
                              value={item.quantity}
                              onValueChange={(value) =>
                                setLineItemsDraft((current) =>
                                  current
                                    ? current.map((row) => (row.id === item.id ? { ...row, quantity: value } : row))
                                    : current
                                )
                              }
                            />
                            <TextField
                              value={item.unit}
                              onValueChange={(value) =>
                                setLineItemsDraft((current) =>
                                  current
                                    ? current.map((row) => (row.id === item.id ? { ...row, unit: value } : row))
                                    : current
                                )
                              }
                            />
                          </div>
                        </td>
                        <td className="av-collection-table__cell is-right">
                          <TextField
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.unitPrice}
                            onValueChange={(value) =>
                              setLineItemsDraft((current) =>
                                current
                                  ? current.map((row) => (row.id === item.id ? { ...row, unitPrice: value } : row))
                                  : current
                              )
                            }
                          />
                        </td>
                        <td className="av-collection-table__cell is-right">
                          <TextField
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.vatRate}
                            onValueChange={(value) =>
                              setLineItemsDraft((current) =>
                                current
                                  ? current.map((row) => (row.id === item.id ? { ...row, vatRate: value } : row))
                                  : current
                              )
                            }
                          />
                        </td>
                        <td className="av-collection-table__cell is-right">
                          {new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency: detail.invoice.currency,
                            maximumFractionDigits: 2
                          }).format(lineTotal)}
                        </td>
                        <td className="av-collection-table__cell is-right">
                          <button className="av-inline-link-button" type="button" onClick={() => moveLineItem(item.id, -1)}>
                            ↑
                          </button>
                          <button className="av-inline-link-button" type="button" onClick={() => moveLineItem(item.id, 1)}>
                            ↓
                          </button>
                          <button className="av-inline-link-button" type="button" onClick={() => handleRemoveLineItem(item.id)}>
                            {t('common.remove')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </CollectionTable>
              </EditorSectionCard>
            ) : null}

            {editorTab === 'document' ? (
              <EditorSectionCard
                title={t('invoices.sections.document')}
                actions={
                  <div className="av-inline-metadata">
                    <button
                      className="av-secondary-button"
                      type="button"
                      disabled={isSyncingOblio}
                      onClick={() => void handleSyncOblio()}
                    >
                      <Icon name="sync" />
                      <span>{t('invoices.actions.syncOblio')}</span>
                    </button>
                    <OverflowMenuButton />
                  </div>
                }
              >
                <div className="av-summary-stack">
                  <ValuePair label={t('invoices.document.pdfAsset')} value={detail.invoice.pdfAssetId ?? t('common.notSet')} />
                  <ValuePair label={t('invoices.document.syncState')} value={model.syncLabel} />
                  <ValuePair label={t('invoices.document.exportedAt')} value={model.exportedLabel} />
                  <ValuePair label={t('invoices.document.syncedAt')} value={model.syncedLabel} />
                  <ValuePair label={t('invoices.document.externalId')} value={detail.invoice.oblioExternalId ?? t('common.notSet')} />
                  {detail.invoice.externalSyncError ? (
                    <p className="av-form-error">{detail.invoice.externalSyncError}</p>
                  ) : null}
                  {detail.externalDocuments.map((document) => (
                    <ValuePair
                      key={document.id}
                      label={`${document.documentKind === 'invoice' ? t('invoices.document.invoice') : t('invoices.document.proforma')} ${document.seriesName}`}
                      value={document.link ? `${document.number} - ${document.link}` : document.number}
                    />
                  ))}
                </div>
              </EditorSectionCard>
            ) : null}
          </>
        }
        rail={
          <EditorSectionCard title={t('invoices.sections.rightRail')} actions={<OverflowMenuButton />}>
            <div className="av-summary-stack">
              <ValuePair label={t('invoices.summary.subtotal')} value={model.subtotalLabel} />
              <ValuePair label={t('invoices.summary.tax')} value={model.taxLabel} />
              <ValuePair label={t('invoices.summary.total')} value={model.grandTotalLabel} />
              <ValuePair label={t('invoices.summary.sync')} value={model.syncLabel} />
            </div>
          </EditorSectionCard>
        }
      />
    </EditorPageLayout>
  );
}
